import { BaseElement, defineElement } from '@webtides/element-js';

class AnimateParallax extends BaseElement {
    _targets = [];

    getCurrentYTranslation($target) {
        const style = window.getComputedStyle($target);
        const matrix = style['transform'] || style.webkitTransform || style.mozTransform;

        // No transform property. Simply return 0 values.
        if (matrix === 'none' || typeof matrix === 'undefined') {
            return 0;
        }

        const matrixValues = matrix.match(/matrix.*\((.+)\)/)[1].split(', ');
        return matrixValues[5];
    }

    properties() {
        return {
            rootMargin: '0px 0px 0px 0px',
            threshold: 0,
            isIntersecting: false,
            selector: '[parallax]',
            parallaxYDistance: 100,
            parallaxXDistance: 0,
            enabledQuery: '(min-width: 1024px)',
            enabled: true,
        };
    }

    connected() {
        this._matcher = window.matchMedia(this.enabledQuery);
        if (this._matcher.matches) {
            this.initialize();
        }
    }

    watch() {
        return {
            enabled: () => {
                if (this.enabled) {
                    this.initialize();
                } else {
                    this.destroy();
                }
            },
        };
    }

    events() {
        return {
            window: {
                resize: () => {
                    if (this._matcher.matches) {
                        this.initialize();
                    } else {
                        this.destroy();
                    }
                },
            },
        };
    }

    setIntersectionObserverForTarget(target, index) {
        target.intersectionObserver = new IntersectionObserver(
            (entries, observer) => {
                entries.forEach(entry => {
                    this._targets[index].isIntersecting = entry.isIntersecting;
                });
            },
            {
                rootMargin: this.rootMargin,
                threshold: this.threshold,
            }
        );

        target.intersectionObserver.observe(target.$element);
    }

    getElementMovingTargetDistance($target) {
        return Number($target.dataset.distance ?? 100);
    }

    getContainerPositionRelativeToScroll($target, scroll = window.pageYOffset) {
        switch ($target.dataset.containerOrigin) {
            case 'top':
                return scroll;
            case 'bottom':
                return scroll + window.innerHeight;
            default:
                return scroll + window.innerHeight / 2;
        }
    }

    getCurrentElementPosition($target) {
        // The center of the target element.
        const boundingClient = $target.getBoundingClientRect();
        const currentTranslation = this.getCurrentYTranslation($target);

        let homePosition = window.pageYOffset + boundingClient.top;
        switch ($target.dataset.parallaxOrigin) {
            case 'top':
                break;
            case 'bottom':
                homePosition += boundingClient.height;
                break;
            default:
                homePosition += boundingClient.height / 2;
        }

        return homePosition - currentTranslation;
    }

    getParallaxBoundingContainer($target) {
        const { parallaxStart, parallaxEnd } = $target.dataset;

        const currentElementPosition = this.getCurrentElementPosition($target);

        const top = currentElementPosition + (parallaxStart ? parseInt(parallaxStart) : this.parallaxYDistance);
        const bottom = currentElementPosition + (parallaxEnd ? parseInt(parallaxEnd) : this.parallaxYDistance);

        return {
            top,
            bottom,
        };
    }

    initialHomePositionCorrection($target) {
        const correction =
            this.getContainerPositionRelativeToScroll($target, 0) - this.getParallaxBoundingContainer($target).top;

        const currentElementPosition = this.getCurrentElementPosition($target);

        // Special case for elements that are inside the first viewport
        if (currentElementPosition < window.innerHeight) {
            return correction;
        }

        return Math.max(correction, 0);
    }

    getCorrectedBoundingContainer(target) {
        const boundingContainer = this.getParallaxBoundingContainer(target.$element);

        return {
            top: boundingContainer.top + target.correction,
            bottom: boundingContainer.bottom + target.correction,
        };
    }

    destroy() {
        if (!this._initialized) {
            return;
        }

        this._initialized = false;

        this._targets.forEach(target => {
            if (target.intersectionObserver) {
                target.intersectionObserver.unobserve(target.$element);
            }
        });

        if (this._animationInterval) {
            clearInterval(this._animationInterval);
        }

        this.stopParallaxListener();
    }

    initialize() {
        if (!this.enabled || this._initialized) {
            return;
        }

        this._initialized = true;

        this._targets = [];

        const $targets = this.querySelectorAll(this.selector);
        $targets.forEach(($target, index) => {
            const homePosition = this.getCurrentElementPosition($target);

            const correction = this.initialHomePositionCorrection($target);

            const target = {
                $element: $target,
                isIntersecting: undefined,
                intersectionObserver: undefined,
                homePosition: homePosition,
                correction,
                lastScrollCalculation: this.getContainerPositionRelativeToScroll($target),
                currentTranslation: 0,
            };

            $target.style.setProperty('transition', 'transform 0.2s');

            this._targets.push(target);
            this.setIntersectionObserverForTarget(target, index);
        });

        this.setAnimationInterval();
        this.startParallaxListener();
    }

    setAnimationInterval() {
        this._animationInterval = setInterval(() => {
            if (!this._hasUpdated) {
                return;
            }

            this._targets.forEach((target, index) => {
                if (!target.isIntersecting) {
                    return;
                }

                const { $element } = target;

                const currentScrollY = this.getContainerPositionRelativeToScroll($element);

                // The distance the target moves
                const targetMovingDistanceY = parseFloat($element.dataset.distance ?? this.parallaxYDistance);
                const targetMovingDistanceX = parseFloat($element.dataset.distanceHorizontal ?? this.parallaxXDistance);

                const boundingContainer = this.getCorrectedBoundingContainer(target);

                // The distance the window is currently scrolled away from the target.
                const distanceToContainerOrigin = currentScrollY - boundingContainer.top;
                const totalAvailableScrollDistance = boundingContainer.bottom - boundingContainer.top;

                let minTranslation = -1;
                let maxTranslation = 1;

                const currentDistanceInPercent = Math.min(
                    maxTranslation,
                    Math.max(minTranslation, distanceToContainerOrigin / totalAvailableScrollDistance)
                );

                const distanceY = targetMovingDistanceY * currentDistanceInPercent;
                const distanceX = targetMovingDistanceX * currentDistanceInPercent;

                requestAnimationFrame(() => {
                    target.$element.style.setProperty('transform', `translate3d(${distanceX}px, ${distanceY}px, 0)`);
                });
            });

            this._hasUpdated = false;
        }, 100);
    }

    onScroll() {
        this._hasUpdated = true;
    }

    startParallaxListener() {
        document.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
    }

    stopParallaxListener() {
        document.removeEventListener('scroll', this.onScroll.bind(this));
    }
}

defineElement('animate-parallax', AnimateParallax);
